import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { FOR_INVESTORS_SECTIONS, FOR_INVESTORS_SUB_SECTIONS } from 'constants/businessToolSections/forInvestors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { AutocompleteControl } from 'components/_basic/Form/Autocomplete';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { GENDER_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { GENDER_OPTIONS_ES } from 'components/BusinessToolForm/constants/es/options_es';
import { GENDER_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { GENDER_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';
import {
  B2B_NAMES,
  B2B_PREFIX,
  B2C_NAMES,
  B2C_PREFIX,
  CONSUMER_NEEDS_NAMES,
  prefix,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/ConsumerNeeds/utils/form';

import { concatWithDot } from 'helpers/string';

export const ConsumerNeedsMarket: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS,
    FOR_INVESTORS_SUB_SECTIONS.FOR_INVESTORS_MARKET_ANALYSIS.CONSUMER_NEEDS,
  );
  const getChosenGenderOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return GENDER_OPTIONS_DEU;

      case 'uk':
        return GENDER_OPTIONS_UA;
      case 'es':
        return GENDER_OPTIONS_ES;

      default:
        return GENDER_OPTIONS;
    }
  };

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('marketAnalysisForm.title')}
          badgeText={t('marketAnalysisForm.consumers.title')}
          section={FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS}
        />
      </FormSectionCard>
      {/* 4.3.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.consumers.profileB2C')}
        collapsible
        aiFieldName={B2C_PREFIX}
        aiFieldType="complex"
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <AutocompleteControl
              control={control}
              id={concatWithDot(B2C_PREFIX, B2C_NAMES.GENDER)}
              name={concatWithDot(B2C_PREFIX, B2C_NAMES.GENDER)}
              label={t('marketAnalysisForm.consumers.gender')}
              options={getChosenGenderOptions(i18n.language)}
              disabled={!canAccess}
              defaultValue={getChosenGenderOptions(i18n.language)[0]}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              id={concatWithDot(B2C_PREFIX, B2C_NAMES.AGE)}
              name={concatWithDot(B2C_PREFIX, B2C_NAMES.AGE)}
              label={t('marketAnalysisForm.consumers.age')}
              disabled={!canAccess}
              maxLength={15}
              rules={{
                pattern: {
                  value: /^[\d-]+$/,
                  message: '',
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id={concatWithDot(B2C_PREFIX, B2C_NAMES.ANNUAL_INCOME)}
              name={concatWithDot(B2C_PREFIX, B2C_NAMES.ANNUAL_INCOME)}
              label={t('marketAnalysisForm.consumers.annualIncome')}
              adornmentType="currency"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              id={concatWithDot(B2C_PREFIX, B2C_NAMES.PROFESSION)}
              name={concatWithDot(B2C_PREFIX, B2C_NAMES.PROFESSION)}
              label={t('marketAnalysisForm.consumers.profession')}
              disabled={!canAccess}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(B2C_PREFIX, B2C_NAMES.PERSONAL_INTERESTS)}
              name={concatWithDot(B2C_PREFIX, B2C_NAMES.PERSONAL_INTERESTS)}
              minRows={4}
              maxLength={500}
              label={t('marketAnalysisForm.consumers.personalInterests')}
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(B2C_PREFIX, B2C_NAMES.PURCHASING_HABITS)}
              name={concatWithDot(B2C_PREFIX, B2C_NAMES.PURCHASING_HABITS)}
              minRows={4}
              maxLength={500}
              label={t('marketAnalysisForm.consumers.purchasingHabits')}
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(B2C_PREFIX, B2C_NAMES.NEEDS_AND_PROBLEMS)}
              name={concatWithDot(B2C_PREFIX, B2C_NAMES.NEEDS_AND_PROBLEMS)}
              minRows={4}
              maxLength={500}
              label={t('marketAnalysisForm.consumers.needsAndProblems')}
              disabled={!canAccess}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      {/* 4.3.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.consumers.profileB2B')}
        collapsible
        aiFieldName={B2B_PREFIX}
        aiFieldType="complex"
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              id={concatWithDot(B2B_PREFIX, B2B_NAMES.INDUSTRY)}
              name={concatWithDot(B2B_PREFIX, B2B_NAMES.INDUSTRY)}
              label={t('marketAnalysisForm.consumers.industry')}
              disabled={!canAccess}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id={concatWithDot(B2B_PREFIX, B2B_NAMES.ANNUAL_REVENUE)}
              name={concatWithDot(B2B_PREFIX, B2B_NAMES.ANNUAL_REVENUE)}
              label={t('marketAnalysisForm.consumers.annualRevenue')}
              adornmentType="currency"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id={concatWithDot(B2B_PREFIX, B2B_NAMES.NUMBER_OF_EMPLOYERS)}
              name={concatWithDot(B2B_PREFIX, B2B_NAMES.NUMBER_OF_EMPLOYERS)}
              label={t('marketAnalysisForm.consumers.numberOfEmployers')}
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              id={concatWithDot(B2B_PREFIX, B2B_NAMES.REGION)}
              name={concatWithDot(B2B_PREFIX, B2B_NAMES.REGION)}
              label={t('marketAnalysisForm.consumers.region')}
              disabled={!canAccess}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(B2B_PREFIX, B2B_NAMES.PURCHASING_CYCLE)}
              name={concatWithDot(B2B_PREFIX, B2B_NAMES.PURCHASING_CYCLE)}
              minRows={4}
              maxLength={500}
              label={t('marketAnalysisForm.consumers.purchasingCycle')}
              disabled={!canAccess}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      {/* 4.3.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.consumers.purchasingDecision')}
        aiFieldName={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.PURCHASING_DECISION)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.PURCHASING_DECISION)}
          name={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.PURCHASING_DECISION)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.3.6 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.consumers.howCustomersSatisfied')}
        aiFieldName={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.HOW_CUSTOMERS_SATISFIED)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.HOW_CUSTOMERS_SATISFIED)}
          name={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.HOW_CUSTOMERS_SATISFIED)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.3.7 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.consumers.demand')}
        aiFieldName={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.DEMAND)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.DEMAND)}
          name={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.DEMAND)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.3.8 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.consumers.customerBaseSize')}
        aiFieldName={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.CUSTOMER_BASE_SIZE)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.CUSTOMER_BASE_SIZE)}
          name={concatWithDot(prefix, CONSUMER_NEEDS_NAMES.CUSTOMER_BASE_SIZE)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
